import React, { useEffect, useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, useApp } from '../../contexts';
import { navigateToExternalRoute, navigateWithReload } from '../../utils';
import { ErrorModal } from '../../components/ErrorModal';
import { API } from '../../services/API/BaseAPI';
import { ErrorsService } from '../../services/ErrorsService';
import { config } from '../../services/Constants/AppConstants';

const UHC_STORE_URL = 'https://member.uhc.com/';
const SUPPORT_URL = `${config.WEBSITE_URL}/#contact`;
const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please contact support.';

type ErrorAction = { label: string; onClick: () => void };
type ErrorPresentation = { message: string; primary: ErrorAction; secondary: ErrorAction };

const SIGN_IN: ErrorAction = {
  label: 'Sign In with email',
  onClick: () => navigateWithReload('/login/'),
};
const CONTACT_SUPPORT: ErrorAction = {
  label: 'Contact support',
  onClick: () => navigateToExternalRoute(SUPPORT_URL),
};
const RETURN_TO_STORE: ErrorAction = {
  label: 'Return to UHC Store',
  onClick: () => navigateToExternalRoute(UHC_STORE_URL),
};

// The member already has an Every Mother account: signing in or support, never a second account.
const EXISTING_MEMBER_ACTIONS = { primary: SIGN_IN, secondary: CONTACT_SUPPORT };
// The UHC assertion itself failed: only the UHC Store or support can fix that.
const UHC_STORE_ACTIONS = { primary: RETURN_TO_STORE, secondary: CONTACT_SUPPORT };

// Codes the API sends back in ?error= when the SSO assertion cannot be turned into a session. Actions must
// lead where the message points; the modal cannot be dismissed, so these two buttons are all the member has.
const SAML_ERRORS: Record<string, ErrorPresentation> = {
  active_membership_with_other_platform: {
    message: 'You already have a membership with us. Please contact support for assistance.',
    ...EXISTING_MEMBER_ACTIONS,
  },
  email_missing: {
    message:
      "We couldn't read your UHC account details. Please return to the UHC Store and try again, or contact support.",
    ...UHC_STORE_ACTIONS,
  },
  unknown: {
    message:
      "We couldn't complete your UHC sign-in. Please return to the UHC Store and try again, or contact support.",
    ...UHC_STORE_ACTIONS,
  },
};
const DEFAULT_ERROR: ErrorPresentation = { message: DEFAULT_ERROR_MESSAGE, ...UHC_STORE_ACTIONS };

function SamlCallback() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const pendingSamlId = searchParams.get('pending_saml_id');
  const pendingLinkId = searchParams.get('pending_link_id');
  const isNewUser = Boolean(Number(searchParams.get('is_new_user')));
  const isLinkedAccount = Boolean(Number(searchParams.get('linked_account')));
  const userError = searchParams.get('error');
  const email = searchParams.get('email');
  const hasPassword = Boolean(Number(searchParams.get('has_password')));
  const error = searchParams.get('error');
  const { login } = useAuth();
  const { setLoading } = useApp();

  const [localError, setLocalError] = useState<ErrorPresentation | null>(null);

  const navigateAfterLogin = useCallback(async () => {
    setLoading(true);
    try {
      if (token) {
        login(token);
        await API.post('auth-cookie');
        navigateWithReload('/app/your-dashboard');
      } else if (pendingSamlId && isNewUser) {
        localStorage.setItem('uhc_pending_saml_id', pendingSamlId);
        if (email) localStorage.setItem('uhc_pending_email', email);
        navigateWithReload(`/members/activate-account/?saml=true`);
      } else if (pendingLinkId) {
        localStorage.setItem('uhc_pending_link_id', pendingLinkId);
        if (email) localStorage.setItem('uhc_pending_email', email);
        if (hasPassword != null) {
          const hasPasswordValue = hasPassword ? 'true' : 'false';
          localStorage.setItem('uhc_pending_link_has_password', hasPasswordValue);
        }

        const isLinkedAccountValue = isLinkedAccount ? 'true' : 'false';
        localStorage.setItem('uhc_is_linked_account', isLinkedAccountValue);

        navigateWithReload(`/members/activate-account/?link=true`);
      } else if (userError) {
        // Own properties only: `?error=constructor` must not resolve through Object.prototype.
        const presentation = Object.prototype.hasOwnProperty.call(SAML_ERRORS, userError)
          ? SAML_ERRORS[userError]
          : undefined;
        if (!presentation) {
          // Stable title: the code comes from the URL, so it must not shape Sentry issue grouping.
          ErrorsService.message('[SamlCallback] Unrecognized SAML error code', {
            extra: { code: userError },
          });
        }
        setLocalError(presentation ?? DEFAULT_ERROR);
      } else {
        throw new Error('Unexpected callback params');
      }
    } catch (e) {
      ErrorsService.message(
        `[SamlCallback] Error during redirect flow: ${e instanceof Error ? e.message : String(e)}`,
        { extra: { error: e, errorParam: error } },
      );
      // Never echo the raw query parameter to the member; it is a machine code at best.
      setLocalError(DEFAULT_ERROR);
    } finally {
      setLoading(false);
    }
  }, [
    setLoading,
    token,
    pendingSamlId,
    isNewUser,
    pendingLinkId,
    userError,
    login,
    email,
    hasPassword,
    isLinkedAccount,
    error,
  ]);

  useEffect(() => {
    navigateAfterLogin();
  }, [navigateAfterLogin]);

  if (localError) {
    return (
      <ErrorModal
        isIcon={false}
        customClassName="error-modal-oauth"
        message={localError.message}
        buttonText={localError.primary.label}
        secondaryButtonText={localError.secondary.label}
        onSubmit={localError.primary.onClick}
        onSecondarySubmit={localError.secondary.onClick}
      />
    );
  }

  return <div />;
}

export default SamlCallback;
